import type {
  DocumentImportResult,
  ImportValidationError,
} from "../components/ImportInvoicesDropzone";

const FIELD_REASONS_ES: Record<string, string> = {
  documento: "no se pudo leer el documento",
  fecha: "la fecha es dudosa o no coincide con el PDF",
  totales: "los totales no cuadran",
  lineas: "las líneas no coinciden con la base imponible",
  cliente: "falta el cliente o no se identificó en tu agenda",
  cif: "el NIF/CIF necesita comprobación",
  numero: "falta el número de factura",
  confidence: "confianza por debajo del 95% (revisión manual)",
  tipo_documento: "es una factura recibida (gasto), no emitida",
};

export function formatImportDisplayName(
  fileName: string,
  extracted?: { numero?: string | null } | null,
  invoiceNumber?: string | null
): string {
  const inv = String(invoiceNumber || "").trim();
  if (inv) return inv;
  const numero = String(extracted?.numero || "").trim();
  if (numero) {
    if (/^(?:fac|inv|fra|fact)[-_\s]/i.test(numero)) return numero;
    return `factura nº ${numero}`;
  }
  return fileName.trim() || "documento";
}

function primaryReason(
  validationErrors?: ImportValidationError[],
  fallbackErrors?: string[]
): string {
  const errors: ImportValidationError[] = [
    ...(validationErrors || []),
    ...(fallbackErrors || []).map((message) => ({ field: "general", message })),
  ];
  if (!errors.length) return "";

  const priority = [
    "documento",
    "fecha",
    "totales",
    "lineas",
    "cliente",
    "cif",
    "numero",
    "confidence",
    "tipo_documento",
  ];

  for (const field of priority) {
    const hit = errors.find((e) => e.field === field);
    if (hit) return FIELD_REASONS_ES[field] || hit.message;
  }
  return errors[0]?.message || "";
}

export function buildReviewUx(
  fileName: string,
  result?: DocumentImportResult | null,
  opts?: { hardFailure?: boolean; apiMessage?: string }
): { title: string; subtitle: string; isHardFailure: boolean } {
  const displayName = formatImportDisplayName(
    fileName,
    result?.extracted,
    result?.invoiceNumber
  );

  if (result?.reviewMessage && result?.reviewReason) {
    return {
      title: `Revisa: ${result.displayName || displayName}`,
      subtitle: result.reviewReason,
      isHardFailure: opts?.hardFailure ?? !result?.extracted,
    };
  }

  if (result?.reviewMessage) {
    const subtitle =
      result.reviewReason ||
      result.reviewMessage.replace(/^Revisa\s+[^:]+:\s*/i, "");
    return {
      title: `Revisa: ${result.displayName || displayName}`,
      subtitle,
      isHardFailure: opts?.hardFailure ?? !result?.extracted,
    };
  }

  const reason =
    primaryReason(result?.validationErrors, result?.errors) ||
    (opts?.apiMessage && !/error desconocido|error de análisis/i.test(opts.apiMessage)
      ? opts.apiMessage
      : "");

  const hardFailure = opts?.hardFailure ?? !result?.extracted;

  if (hardFailure && !result?.extracted) {
    const subtitle =
      reason ||
      (opts?.apiMessage?.match(/timeout|tardó|disponible/i)
        ? "el análisis tardó demasiado; inténtalo de nuevo"
        : "no se pudo leer el documento; inténtalo de nuevo");
    return { title: `Revisa: ${displayName}`, subtitle, isHardFailure: true };
  }

  const subtitle =
    reason || "hay datos que conviene revisar antes de importar";

  return {
    title: `Revisa: ${displayName}`,
    subtitle,
    isHardFailure: false,
  };
}

export function mapApiErrorMessage(
  fileName: string,
  message?: string,
  error?: string
): string {
  const blob = `${message || ""} ${error || ""}`.toLowerCase();
  const file = fileName.trim() || "documento";
  if (/timeout|tardó|504|503|502|disponible/i.test(blob)) {
    return `Revisa ${file}: el análisis tardó; se reintentará archivo por archivo`;
  }
  if (/nvidia|api_key|no configurada/i.test(blob)) {
    return `Revisa ${file}: no se pudo leer el documento; inténtalo de nuevo`;
  }
  if (message && !/error desconocido/i.test(message)) {
    return message;
  }
  return `Revisa ${file}: no se pudo analizar; inténtalo de nuevo`;
}

export function friendlyConfirmError(
  fileName: string,
  result?: DocumentImportResult | null,
  fallback?: string
): string {
  const ux = buildReviewUx(fileName, result, { hardFailure: !result?.extracted });
  if (ux.subtitle) return `${ux.title} — ${ux.subtitle}`;
  return fallback || `Revisa: ${formatImportDisplayName(fileName, result?.extracted)}`;
}
